import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { RetellWebClient } from 'retell-client-js-sdk';
import RandomWaveViz from './RandomWaveViz';
import ReactGA from "react-ga4";
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { getCorsHeaders } from './utils/corsHeaders';

const DEFAULT_PHONE_NUMBER = '+14152334023';
const DEFAULT_AGENT_ID = '893c8f270b4a5fb7bebc5146910b420e';

// Futuristic glassmorphic styles
const styles = {
    header: {
        textAlign: 'center',
        marginBottom: '40px'
    },
    title: {
        fontSize: '36px',
        fontWeight: '700',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: '10px',
        lineHeight: '1.2'
    },
    subtitle: {
        color: '#cbd5e1',
        fontSize: '18px',
        marginBottom: '30px'
    },
    mainButton: {
        width: '100%',
        padding: '20px 40px',
        borderRadius: '50px',
        border: 'none',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        fontSize: '18px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
        position: 'relative',
        overflow: 'hidden'
    },
    mainButtonHover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(99, 102, 241, 0.4)'
    },
    mainButtonActive: {
        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
        transform: 'scale(0.98)'
    },
    loadingSpinner: {
        width: '20px',
        height: '20px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: '2px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    finishButton: {
        marginTop: '20px',
        padding: '12px 30px',
        borderRadius: '25px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: 'white',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    tipsContainer: {
        marginTop: '40px',
        padding: '25px',
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px'
    },
    tipsTitle: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#a78bfa',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '20px'
    },
    tipItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        marginBottom: '16px',
        color: '#e2e8f0'
    },
    tipNumber: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: '600',
        color: 'white',
        flexShrink: 0
    },
    tipText: {
        fontSize: '14px',
        lineHeight: '1.6',
        color: '#cbd5e1'
    },
    shareContainer: {
        marginTop: '40px',
        textAlign: 'center',
        padding: '30px',
        background: 'rgba(99, 102, 241, 0.1)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: '16px'
    },
    shareTitle: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#f8fafc',
        marginBottom: '20px'
    },
    shareButton: {
        padding: '12px 24px',
        borderRadius: '25px',
        border: 'none',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)'
    },
    socialButton: {
        padding: '12px',
        borderRadius: '12px',
        border: 'none',
        color: 'white',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        minWidth: '44px',
        minHeight: '44px'
    },
    callerInput: {
        marginBottom: '20px',
        padding: '15px',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    callerLabel: {
        display: 'block',
        marginBottom: '8px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#cbd5e1'
    },
    callerInputField: {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        background: 'rgba(255, 255, 255, 0.05)',
        color: 'white',
        fontSize: '16px',
        outline: 'none',
        transition: 'all 0.3s ease'
    }
};

const ShareTestCallCard = ({
    practiceName,
    phoneNumber,
    isExpanded,
    setIsExpanded,
    hasTriggeredConfetti,
    setHasTriggeredConfetti,
    isCustomPractice,
    agentID,
    vipId,
    vipConfig,
    label,
    token, 
    synthId
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [retellWebClient, setRetellWebClient] = useState(() => new RetellWebClient());
    const [retellStartCallData, setRetellStartCallData] = useState(null);
    const [callInProgress, setCallInProgress] = useState(false);
    const [isTalking, setIsTalking] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const hasFetchedRef = useRef(false);
    const [rating, setRating] = useState(0);
    const [callEnded, setCallEnded] = useState(false);
    const navigate = useNavigate();
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [feedbackData, setFeedbackData] = useState(null)
    const [isFeedback, setIsFeedback] =  useState('false')
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [shareSuccess, setShareSuccess] = useState(false);
    const [searchParams] = useSearchParams();
    const [callerName, setCallerName] = useState('');
    const [showCallerInput, setShowCallerInput] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    
    // Audio player states for post-call
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef(null);
    const [callId, setCallId] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [recordingLoading, setRecordingLoading] = useState(false);
    const [recordingError, setRecordingError] = useState(null);
    
    // Check for test mode from URL params
    const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
    const isTestMode = urlParams.get('test') === 'true';

    console.log(vipConfig)
    
    // Clear stale session storage on component mount
    useEffect(() => {
        // Clear any leftover call data from previous sessions
        // This prevents the post-call screen from showing on page reload
        if (!isTestMode) {
            sessionStorage.removeItem('last_call_id');
            console.log('Cleared stale session storage on mount');
        }
    }, []); // Run only once on mount
    
    // Handle direct recording access via call_id parameter
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
        const callIdFromUrl = urlParams.get('call_id');
        
        if (callIdFromUrl && !callEnded && !isTestMode) {
            // Someone is accessing a shared recording directly
            console.log('Direct recording access for call_id:', callIdFromUrl);
            setCallId(callIdFromUrl);
            setCallEnded(true);
            setCallInProgress(false);
            sessionStorage.setItem('last_call_id', callIdFromUrl);
            // Start fetching the recording
            fetchRecordingWithRetry(callIdFromUrl);
        }
    }, []); // Run only once on mount
    
    // Auto-enable test mode ONLY when explicitly requested
    useEffect(() => {
        if (isTestMode && !callEnded) {
            // Only simulate in test mode
            setTimeout(() => {
                setCallEnded(true);
                setCallInProgress(false);
                // Set a test call ID and audio URL
                const testCallId = 'test_call_' + Date.now();
                setCallId(testCallId);
                sessionStorage.setItem('last_call_id', testCallId);
                // Use a sample audio file for testing
                setAudioUrl('https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3');
                console.log('Test mode: Simulating call ended with ID:', testCallId);
            }, 1000);
        }
    }, [isTestMode]);
    
    // Reset function to clear all states
    const handleReset = () => {
        // Clear all call-related states
        setCallEnded(false);
        setCallInProgress(false);
        setCallId(null);
        setAudioUrl(null);
        setRecordingLoading(false);
        setRecordingError(null);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setIsTalking(false);
        setIsLoading(false);
        setRetellWebClient(new RetellWebClient());
        setRetellStartCallData(null);
        
        // Clear session storage
        sessionStorage.removeItem('last_call_id');
        sessionStorage.removeItem('synth_id');
        
        // Reset the hasFetched ref
        hasFetchedRef.current = false;
        
        console.log('State reset - ready for new call');
    };

    useEffect(() => {
      const postToken = async () => {
        if (feedbackData) {
          setIsLoading(true);
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/register-lead`, {
              method: 'POST',
              headers: getCorsHeaders(),
              body: JSON.stringify({
                  name: feedbackData.name,
                  phone: feedbackData.phoneNumber.toString() ? feedbackData.phoneNumber : "+11234567890",
                  email: feedbackData.email,
                  token: token
                }),
            });
            const data = await response.json();
            console.log('Response from API:', data);
            
          } catch (error) {
            console.error('Error posting data:', error);
            
          } finally {
            setIsLoading(false);
          }
        }
      };
      postToken();
    }, [feedbackData, token]); 

    useEffect(() => {
        const requireCallerName = searchParams.get('caller_name');
        const token = searchParams.get('token');
        setShowCallerInput(requireCallerName === 'true');
    }, [searchParams]);

    const fetchCallConfiguration = useCallback(async () => {
        if (hasFetchedRef.current) return retellStartCallData;
        
        const agentId = vipConfig?.agent_id || DEFAULT_AGENT_ID;
        
        try {
            const url = `${process.env.REACT_APP_API_URL}/create-onboard-web-call/${agentId}`;
            const headers = {
                'Content-Type': 'application/json'
            };
            const body = {
                agent_id: agentId,
                metadata: {}
            };
            
            if (callerName) {
                body.metadata.caller_name = callerName;
            }
            
            if (label) {
                body.metadata.label = label;
            }
            
            if (synthId) {
                body.metadata.synth_id = synthId;
            }
            
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(body)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();
            setRetellStartCallData(data);
            hasFetchedRef.current = true;
            
            // Store call_id if available
            if (data && data.results && data.results.call_id) {
                setCallId(data.results.call_id);
                sessionStorage.setItem('last_call_id', data.results.call_id);
                console.log('Call ID stored:', data.results.call_id);
            }
            
            return data;
        } catch (error) {
            console.error('Error fetching call configuration:', error);
            return null;
        }
    }, [vipConfig, synthId, callerName, label, retellStartCallData]);

    const checkAudioPermissions = async () => {
        try {
            await navigator.mediaDevices.getUserMedia({ audio: true });
            return true;
        } catch (err) {
            console.error("Error accessing microphone:", err);
            return false;
        }
    };

    // Function to fetch recording with retry logic
    const fetchRecordingWithRetry = async (callId, retryCount = 0, maxRetries = 10) => {
        setRecordingLoading(true);
        setRecordingError(null);
        
        try {
            const apiUrl = `https://api.app.yib.io/registration/get-call-recording/${callId}`;
            console.log(`Fetching recording from: ${apiUrl} (attempt ${retryCount + 1}/${maxRetries})`);
            
            const response = await fetch(apiUrl);
            const data = await response.json();
            
            if (data.available && data.recording_url) {
                // Recording is ready!
                console.log('Recording available:', data.recording_url);
                setAudioUrl(data.recording_url);
                setRecordingLoading(false);
            } else if (retryCount < maxRetries) {
                // Recording not ready yet, retry after delay
                console.log('Recording not ready, retrying in 3 seconds...');
                setTimeout(() => {
                    fetchRecordingWithRetry(callId, retryCount + 1, maxRetries);
                }, 3000); // Retry every 3 seconds
            } else {
                // Max retries reached
                console.error('Recording not available after maximum retries');
                setRecordingError('Recording is taking longer than expected. Please try again later.');
                setRecordingLoading(false);
            }
        } catch (error) {
            console.error('Error fetching recording:', error);
            if (retryCount < maxRetries) {
                // Retry on error
                setTimeout(() => {
                    fetchRecordingWithRetry(callId, retryCount + 1, maxRetries);
                }, 3000);
            } else {
                setRecordingError('Failed to fetch recording. Please try again later.');
                setRecordingLoading(false);
            }
        }
    };

    const handleCallClick = async () => {
        if (callInProgress || isLoading) return;

        const hasPermission = await checkAudioPermissions();
        if (!hasPermission) {
            const confirmCall = window.confirm("No microphone detected. Would you like to call the phone number instead?");
            if (confirmCall) {
                window.location.href = `tel:${phoneNumber || DEFAULT_PHONE_NUMBER}`;
            }
            return;
        }

        try {
            setIsLoading(true);

            const newRetellWebClient = new RetellWebClient();
            setRetellWebClient(newRetellWebClient);

            const data = await fetchCallConfiguration();
            if (!data || !data.results || !data.results.access_token) {
                throw new Error("Failed to obtain access token");
            }
            
            // Store call_id if not already stored
            if (data.results.call_id && !callId) {
                setCallId(data.results.call_id);
                sessionStorage.setItem('last_call_id', data.results.call_id);
            }

            await newRetellWebClient.startCall({
                accessToken: data.results.access_token,
            });

            setCallInProgress(true);
        } catch (error) {
            console.error("Error starting call:", error);
            alert("There was an error starting the call. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    const stopCall = useCallback(() => {
        try {
            retellWebClient.stopCall();
            setCallInProgress(false);
            setIsTalking(false);
            hasFetchedRef.current = false;
            setCallEnded(true);
            setTimeout(() => {
                setIsLoading(false);
            }, 2000);
        } catch (error) {
            console.error("Error stopping call:", error);
        }
    }, [retellWebClient]);

    useEffect(() => {
        retellWebClient.on("call_started", () => {
            console.log("call started");
        });

        retellWebClient.on("call_ended", () => {
            console.log("call ended");
            setCallInProgress(false);
            setCallEnded(true);
            
            // Start polling for recording after call ends
            const storedCallId = sessionStorage.getItem('last_call_id');
            if (storedCallId) {
                fetchRecordingWithRetry(storedCallId);
            }
        });

        retellWebClient.on("agent_start_talking", () => {
            setIsTalking(true);
        });

        retellWebClient.on("agent_stop_talking", () => {
            setIsTalking(false);
        });

        retellWebClient.on("audio", (audio) => {
            // Handle audio if needed
        });

        retellWebClient.on("update", (update) => {
            // Handle update if needed
        });

        retellWebClient.on("metadata", (metadata) => {
            // Handle metadata if needed
        });

        retellWebClient.on("error", (error) => {
            console.error("An error occurred:", error);
            setCallInProgress(false);
        });

        return () => {
            retellWebClient.stopCall();
        };
    }, [retellWebClient]);

    // Remove automatic fetch - we'll fetch when the button is clicked

    const isDefaultAgent = useMemo(() => agentID === DEFAULT_AGENT_ID, [agentID]);
    const hasCustomPracticeName = useMemo(() => practiceName && practiceName.toLowerCase() !== 'your practice', [practiceName]);

    useEffect(() => {
        if (callEnded) {
            QRCode.toDataURL(window.location.href)
                .then(url => {
                    setQrCodeUrl(url);
                })
                .catch(err => {
                    console.error('Error generating QR code:', err);
                });
            
            // Get call ID from retell or session
            const storedCallId = callId || sessionStorage.getItem('last_call_id');
            if (storedCallId && !audioUrl && !recordingLoading) {
                if (!callId) setCallId(storedCallId);
                // Fetch recording with retry logic
                fetchRecordingWithRetry(storedCallId);
            }
        }
    }, [callEnded, callId, audioUrl]);

    const handleShare = async () => {
        // Build the share URL based on whether we're sharing the agent or the recording
        let shareUrl;
        if (callEnded && callId) {
            // Share the recording - include call_id in URL
            const baseUrl = window.location.href.split('?')[0];
            const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
            urlParams.set('call_id', callId);
            shareUrl = `${baseUrl}?${urlParams.toString()}`;
        } else {
            // Share the agent - just the base URL with token
            const baseUrl = window.location.href.split('?')[0];
            const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
            urlParams.delete('call_id'); // Remove call_id if present
            shareUrl = `${baseUrl}?${urlParams.toString()}`;
        }

        // Always just copy to clipboard (don't use navigator.share which opens the dialog)
        try {
            await navigator.clipboard.writeText(shareUrl);
            setShareSuccess(true);
            setTimeout(() => setShareSuccess(false), 2000);
            console.log('Copied to clipboard:', shareUrl);
        } catch (err) {
            console.log('Error copying to clipboard:', err);
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = shareUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setShareSuccess(true);
            setTimeout(() => setShareSuccess(false), 2000);
        }
    };

    const renderContent = () => {
        if (callInProgress) {
            return (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <RandomWaveViz isTalking={isTalking} />
                    <button
                        style={styles.finishButton}
                        onClick={stopCall}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        Finish
                    </button>
                </div>
            );
        }

        if (callEnded) {
            const handlePlayPause = () => {
                if (audioRef.current) {
                    if (isPlaying) {
                        audioRef.current.pause();
                    } else {
                        audioRef.current.play();
                    }
                    setIsPlaying(!isPlaying);
                }
            };
            
            const handleTimeUpdate = () => {
                if (audioRef.current) {
                    setCurrentTime(audioRef.current.currentTime);
                }
            };
            
            const handleLoadedMetadata = () => {
                if (audioRef.current) {
                    setDuration(audioRef.current.duration);
                }
            };
            
            const formatTime = (time) => {
                const minutes = Math.floor(time / 60);
                const seconds = Math.floor(time % 60);
                return `${minutes}:${seconds.toString().padStart(2, '0')}`;
            };
            
            const handleSeek = (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = x / rect.width;
                if (audioRef.current) {
                    audioRef.current.currentTime = percentage * duration;
                }
            };
            
            const handleDownload = async () => {
                if (audioUrl) {
                    try {
                        const response = await fetch(audioUrl);
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `recording_${callId || 'call'}.mp3`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                    } catch (error) {
                        console.error('Error downloading recording:', error);
                    }
                }
            };
            
            const handleSocialShare = (platform) => {
                // Build the share URL with call_id for recording sharing
                const baseUrl = window.location.href.split('?')[0];
                const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
                urlParams.set('call_id', callId);
                const shareUrlWithRecording = `${baseUrl}?${urlParams.toString()}`;
                const shareTextForRecording = `Check out my conversation with ${vipConfig["agent_name"] || "an AI agent"}`;
                
                let url;
                switch(platform) {
                    case 'twitter':
                        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTextForRecording)}&url=${encodeURIComponent(shareUrlWithRecording)}`;
                        break;
                    case 'linkedin':
                        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrlWithRecording)}`;
                        break;
                    case 'facebook':
                        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrlWithRecording)}`;
                        break;
                    case 'email':
                        url = `mailto:?subject=${encodeURIComponent(shareTextForRecording)}&body=${encodeURIComponent(shareUrlWithRecording)}`;
                        break;
                }
                if (platform === 'email') {
                    window.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
            };
            
            // Responsive breakpoints
            const isMobileView = window.innerWidth < 768;
            const isSmallMobile = window.innerWidth < 480;
            
            return (
                <div style={{ 
                    width: '100%', 
                    maxWidth: '800px', 
                    margin: '0 auto',
                    padding: isMobileView ? '20px 15px' : '40px 20px',
                    boxSizing: 'border-box'
                }}>
                    {/* Header with icon */}
                    <div style={{ 
                        textAlign: 'center',
                        marginBottom: '30px'
                    }}>
                        <div style={{
                            width: isMobileView ? '50px' : '60px',
                            height: isMobileView ? '50px' : '60px',
                            margin: '0 auto 20px',
                            background: 'rgba(99, 102, 241, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <svg style={{ width: isMobileView ? '24px' : '30px', height: isMobileView ? '24px' : '30px', color: '#8b5cf6' }} fill="currentColor" viewBox="0 0 20 20">
                                <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                            </svg>
                        </div>
                        <h1 style={{
                            fontSize: isMobileView ? '24px' : '32px',
                            fontWeight: '700',
                            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            marginBottom: '10px',
                            lineHeight: isMobileView ? '1.3' : '1.2'
                        }}>
                            {recordingLoading ? 'Processing Your Recording...' : 
                             recordingError ? 'Recording Unavailable' :
                             'Your Recording is Ready!'}
                        </h1>
                        <p style={{ color: '#94a3b8', fontSize: isMobileView ? '14px' : '16px', padding: isMobileView ? '0 10px' : '0' }}>
                            {recordingLoading ? 'Please wait while we prepare your recording...' :
                             recordingError ? recordingError :
                             `Your personalized conversation with ${vipConfig["agent_name"] || "our AI agent"}`}
                        </p>
                    </div>

                    {/* Recording Info Card */}
                    <div style={{
                        background: 'rgba(99, 102, 241, 0.1)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        borderRadius: '16px',
                        padding: isMobileView ? '15px' : '20px',
                        marginBottom: '20px'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: isMobileView ? 'column' : 'row',
                            justifyContent: isMobileView ? 'flex-start' : 'space-between', 
                            alignItems: isMobileView ? 'stretch' : 'center',
                            marginBottom: '15px',
                            gap: isMobileView ? '12px' : '0'
                        }}>
                            <div>
                                <p style={{ 
                                    color: '#94a3b8', 
                                    fontSize: isMobileView ? '12px' : '14px', 
                                    marginBottom: '4px',
                                    wordBreak: 'break-all'
                                }}>
                                    Call ID: {callId ? (isMobileView ? callId.substring(0, 15) + '...' : callId || 'call_' + Date.now()) : 'call_' + Date.now()}
                                </p>
                                <p style={{ color: '#cbd5e1', fontSize: isMobileView ? '14px' : '16px' }}>
                                    Duration: {formatTime(duration || 0)}
                                </p>
                            </div>
                            <button
                                onClick={handleDownload}
                                style={{
                                    padding: isMobileView ? '8px 16px' : '10px 20px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                    background: 'rgba(99, 102, 241, 0.2)',
                                    color: '#a78bfa',
                                    fontSize: isMobileView ? '12px' : '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    transition: 'all 0.2s ease',
                                    alignSelf: isMobileView ? 'flex-start' : 'auto'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
                                }}
                            >
                                <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Download
                            </button>
                        </div>
                    </div>

                    {/* Audio Player */}
                    <div style={{
                        background: 'rgba(30, 27, 75, 0.6)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '20px',
                        padding: isMobileView ? '20px 15px' : '30px',
                        marginBottom: '30px'
                    }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: isMobileView ? '8px' : '20px',
                                marginBottom: '20px',
                                flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
                                justifyContent: isSmallMobile ? 'center' : 'flex-start'
                            }}>
                                <button
                                    onClick={() => {
                                        if (audioRef.current) {
                                            audioRef.current.currentTime = Math.max(0, currentTime - 10);
                                        }
                                    }}
                                    style={{
                                        padding: isMobileView ? '10px' : '12px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: 'rgba(99, 102, 241, 0.2)',
                                        color: '#a78bfa',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0
                                    }}
                                >
                                    <svg style={{ width: isMobileView ? '16px' : '20px', height: isMobileView ? '16px' : '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M8.445 14.832A1 1 0 0010 14v-4a1 1 0 00-1.555-.832L5 11.798V8a1 1 0 00-2 0v4a1 1 0 00.553.894l4 2a1 1 0 00.894 0z"/>
                                        <path d="M14.445 14.832A1 1 0 0016 14v-4a1 1 0 00-1.555-.832L11 11.798V8a1 1 0 00-2 0v4a1 1 0 00.553.894l4 2a1 1 0 00.894 0z"/>
                                    </svg>
                                </button>
                                
                                <button
                                    onClick={handlePlayPause}
                                    style={{
                                        padding: isMobileView ? '14px' : '16px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
                                        flexShrink: 0
                                    }}
                                >
                                    {isPlaying ? (
                                        <svg style={{ width: isMobileView ? '20px' : '24px', height: isMobileView ? '20px' : '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg style={{ width: isMobileView ? '20px' : '24px', height: isMobileView ? '20px' : '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                                
                                <button
                                    onClick={() => {
                                        if (audioRef.current) {
                                            audioRef.current.currentTime = Math.min(duration, currentTime + 10);
                                        }
                                    }}
                                    style={{
                                        padding: isMobileView ? '10px' : '12px',
                                        borderRadius: '50%',
                                        border: 'none',
                                        background: 'rgba(99, 102, 241, 0.2)',
                                        color: '#a78bfa',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0
                                    }}
                                >
                                    <svg style={{ width: isMobileView ? '16px' : '20px', height: isMobileView ? '16px' : '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M4.555 5.168A1 1 0 003 6v4a1 1 0 001.555.832L8 8.202V12a1 1 0 002 0V8a1 1 0 00-.555-.894l-4-2a1 1 0 00-.89 0z"/>
                                        <path d="M10.555 5.168A1 1 0 009 6v4a1 1 0 001.555.832L14 8.202V12a1 1 0 002 0V8a1 1 0 00-.555-.894l-4-2a1 1 0 00-.89 0z"/>
                                    </svg>
                                </button>
                                
                                <div style={{ 
                                    flex: 1,
                                    width: isSmallMobile ? '100%' : 'auto',
                                    order: isSmallMobile ? 4 : 0
                                }}>
                                    <div 
                                        style={{
                                            height: '6px',
                                            background: 'rgba(99, 102, 241, 0.2)',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            position: 'relative'
                                        }}
                                        onClick={handleSeek}
                                    >
                                        <div style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            height: '100%',
                                            width: `${(currentTime / duration) * 100 || 0}%`,
                                            background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
                                            borderRadius: '3px',
                                            transition: 'width 0.1s ease'
                                        }}></div>
                                    </div>
                                </div>
                                
                                <div style={{ 
                                    color: '#cbd5e1', 
                                    fontSize: isMobileView ? '12px' : '14px',
                                    minWidth: isMobileView ? '60px' : '80px',
                                    textAlign: 'right',
                                    order: isSmallMobile ? 5 : 0,
                                    flexShrink: 0
                                }}>
                                    {formatTime(currentTime)} / {formatTime(duration || 0)}
                                </div>
                            </div>
                        </div>
                        
                        {audioUrl && (
                            <audio
                                ref={audioRef}
                                src={audioUrl}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onEnded={() => setIsPlaying(false)}
                            />
                        )}
                    </div>

                    {/* Share Section */}
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <h3 style={{
                            fontSize: isMobileView ? '18px' : '20px',
                            fontWeight: '600',
                            color: '#f8fafc',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                            Share your Recording
                        </h3>
                        
                        <div style={{ 
                            display: 'flex', 
                            gap: isMobileView ? '8px' : '12px', 
                            justifyContent: 'center',
                            marginBottom: '20px',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={() => handleSocialShare('twitter')}
                                style={{
                                    padding: isMobileView ? '10px 16px' : '12px 20px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: '#000',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease',
                                    fontSize: isMobileView ? '12px' : '14px',
                                    fontWeight: '600'
                                }}
                            >
                                X
                            </button>
                            
                            <button
                                onClick={() => handleSocialShare('facebook')}
                                style={{
                                    padding: isMobileView ? '10px 16px' : '12px 20px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: '#1877f2',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease',
                                    fontSize: isMobileView ? '12px' : '14px',
                                    fontWeight: '600'
                                }}
                            >
                                <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                                Facebook
                            </button>
                            
                            <button
                                onClick={() => handleSocialShare('linkedin')}
                                style={{
                                    padding: isMobileView ? '10px 16px' : '12px 20px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: '#0077b5',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease',
                                    fontSize: isMobileView ? '12px' : '14px',
                                    fontWeight: '600'
                                }}
                            >
                                <svg style={{ width: '16px', height: '16px' }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                                LinkedIn
                            </button>
                            
                            <button
                                onClick={() => handleSocialShare('email')}
                                style={{
                                    padding: isMobileView ? '10px 16px' : '12px 20px',
                                    borderRadius: '12px',
                                    border: 'none',
                                    background: '#ea580c',
                                    color: 'white',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s ease',
                                    fontSize: isMobileView ? '12px' : '14px',
                                    fontWeight: '600'
                                }}
                            >
                                <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Email
                            </button>
                        </div>
                        
                        <button
                            onClick={handleShare}
                            style={{
                                padding: isMobileView ? '12px 24px' : '14px 30px',
                                borderRadius: '25px',
                                border: '1px solid rgba(99, 102, 241, 0.3)',
                                background: 'transparent',
                                color: '#a78bfa',
                                fontSize: isMobileView ? '14px' : '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.3s ease',
                                marginBottom: '20px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            {shareSuccess ? 'Link Copied!' : 'Copy Recording Link'}
                        </button>
                    </div>
                    
                    {/* Action Buttons */}
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px',
                        marginBottom: '20px'
                    }}>
                        <button
                            onClick={handleReset}
                            style={{
                                width: '100%',
                                padding: isMobileView ? '14px' : '16px',
                                borderRadius: '12px',
                                border: 'none',
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)',
                                color: 'white',
                                fontSize: isMobileView ? '14px' : '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            Start New Call
                        </button>
                        
                        <a
                            href="https://yobi.app"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                width: '100%',
                                padding: '16px',
                                borderRadius: '12px',
                                border: '1px solid rgba(99, 102, 241, 0.3)',
                                background: 'rgba(99, 102, 241, 0.1)',
                                color: '#a78bfa',
                                fontSize: '16px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                textAlign: 'center',
                                textDecoration: 'none',
                                display: 'block',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                            }}
                        >
                            Learn more about Yobi →
                        </a>
                    </div>
                </div>
            );
        }

        return (
            <>
                {showCallerInput && (
                    <div style={styles.callerInput}>
                        <label style={styles.callerLabel}>
                            Your Name (Optional)
                        </label>
                        <input
                            type="text"
                            placeholder="Enter your name"
                            value={callerName}
                            onChange={(e) => setCallerName(e.target.value)}
                            style={styles.callerInputField}
                            onFocus={(e) => {
                                e.target.style.border = '1px solid rgba(99, 102, 241, 0.5)';
                                e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                            }}
                            onBlur={(e) => {
                                e.target.style.border = '1px solid rgba(255, 255, 255, 0.2)';
                                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                            }}
                        />
                    </div>
                )}
                <button
                    style={{
                        ...styles.mainButton,
                        ...(isHovered ? styles.mainButtonHover : {}),
                        ...(callInProgress ? styles.mainButtonActive : {})
                    }}
                    onClick={handleCallClick}
                    disabled={isLoading || (showCallerInput && !callerName)}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {isLoading ? (
                        <>
                            <div style={styles.loadingSpinner}></div>
                            <span>Connecting...</span>
                        </>
                    ) : (
                        <>
                            <svg 
                                style={{ width: '24px', height: '24px' }}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth="2" 
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                            </svg>
                            <span>Talk Now</span>
                        </>
                    )}
                </button>
                <div style={styles.tipsContainer}>
                    <div style={styles.tipsTitle}>Tips</div>
                    <div style={styles.tipItem}>
                        <div style={styles.tipNumber}>1</div>
                        <p style={styles.tipText}>
                            {vipConfig["agent_name"] ? 
                                `${vipConfig["agent_name"]} is an AI Teammate created by ${vipConfig["company_name"]}` :
                                `This is an AI Teammate created by ${vipConfig["company_name"]}`}
                        </p>
                    </div>
                    <div style={styles.tipItem}>
                        <div style={styles.tipNumber}>2</div>
                        <p style={styles.tipText}>
                            Speak naturally - just like you would in a normal conversation
                        </p>
                    </div>
                    <div style={styles.tipItem}>
                        <div style={styles.tipNumber}>3</div>
                        <p style={styles.tipText}>
                            Feel free to ask questions or share your thoughts
                        </p>
                    </div>
                    <div style={styles.tipItem}>
                        <div style={styles.tipNumber}>4</div>
                        <p style={styles.tipText}>
                            You can end the conversation at any time by clicking "Finish"
                        </p>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    {vipConfig["agent_name"] ? (
                        <>Talk to {vipConfig["agent_name"]}</>
                    ) : (
                        <>Talk to an AI</>
                    )}
                </h1>
                {vipConfig["company_name"] && (
                    <div style={{ color: '#a78bfa', fontSize: '16px' }}>
                        created by {vipConfig["company_name"]}
                    </div>
                )}
            </div>
            
            {renderContent()}

            {/* Add keyframes for animations */}
            <style jsx>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};
    
export default React.memo(ShareTestCallCard);